<script lang="ts">
  /**
   * ScoreDisplay - Shows current score and streak
   */

  import { fly } from 'svelte/transition';

  // Props
  interface Props {
    score: number;
    streak?: number;
  }

  let { score, streak = 0 }: Props = $props();

  // Track score changes for animation
  let previousScore = $state(score);
  let showScoreIncrease = $state(false);
  let scoreIncrease = $state(0);

  $effect(() => {
    if (score > previousScore) {
      scoreIncrease = score - previousScore;
      showScoreIncrease = true;
      previousScore = score;

      // Hide after animation
      setTimeout(() => {
        showScoreIncrease = false;
      }, 500);
    }
  });
</script>

<div class="score-display">
  <div class="score-value">{score}</div>

  {#if streak >= 3}
    <div class="streak-indicator" transition:fly={{ y: -10, duration: 300 }}>
      <span class="fire-icon">🔥</span>
      <span class="streak-number">{streak}</span>
    </div>
  {/if}

  {#if showScoreIncrease}
    <div
      class="score-increase"
      transition:fly={{ y: -30, duration: 500 }}
    >
      +{scoreIncrease}
    </div>
  {/if}
</div>

<style>
  .score-display {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-sm);
  }

  .score-value {
    font-size: 36px;
    font-weight: bold;
    color: var(--color-bright-yellow);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  .streak-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: rgba(239, 68, 68, 0.9);
    border-radius: 12px;
    animation: streakPulse 0.5s ease-out;
  }

  .fire-icon {
    font-size: 20px;
  }

  .streak-number {
    font-size: 18px;
    font-weight: bold;
    color: white;
  }

  @keyframes streakPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  .score-increase {
    position: absolute;
    top: -10px;
    right: 0;
    font-size: 24px;
    font-weight: bold;
    color: var(--color-success);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    pointer-events: none;
  }

  /* Responsive sizing */
  @media (max-width: 767px) {
    .score-value {
      font-size: 28px;
    }

    .fire-icon {
      font-size: 18px;
    }

    .streak-number {
      font-size: 16px;
    }

    .score-increase {
      font-size: 20px;
    }
  }
</style>
